import moment from "moment";
import { getBacktestBalance } from "../../../utils/backtestAccountUtils";

const getFilteredTradesByAccount = (trades, accountId) => {
  if (!accountId || accountId === "All") {
    return trades;
  }

  if (accountId === "All Personal") {
    return trades.filter(trade => trade.equityType === "Personal Capital");
  }

  if (accountId === "All Investor") {
    return trades.filter(trade => trade.equityType === "Investor Capital");
  }

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const capitalAccounts = user.capitalAccounts || [];
  const selectedAccount = capitalAccounts.find(acc => acc.id === accountId);

  if (!selectedAccount) {
    return trades;
  }

  return trades.filter(trade => {
    if (trade.accountId) {
      return trade.accountId === accountId;
    }
    if (selectedAccount.type === "Personal") {
      return trade.equityType === "Personal Capital";
    } else if (selectedAccount.type === "Investor") {
      return trade.equityType === "Investor Capital";
    }
    return false;
  });
};

const getStartingBalance = (mode, backtestingMode, allYearsOfTrades, selectedYear) => {
  if (backtestingMode) {
    return getBacktestBalance();
  }

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const personal = mode === "Personal Capital";
  const investor = mode === "Investor Capital";
  const firstYear = allYearsOfTrades?.[0];

  if (personal) {
    if (selectedYear === firstYear || selectedYear === "0") {
      return user.startingBalancePC || 0;
    } else {
      const rollingBalances = JSON.parse(localStorage.getItem("rollingBalancesPC") || "[]");
      return rollingBalances.find(
        (element) => element.title.includes(Number(selectedYear) - 1)
      )?.value || user.startingBalancePC || 0;
    }
  } else if (investor) {
    if (selectedYear === firstYear || selectedYear === "0") {
      return user.startingBalanceIC || 0;
    } else {
      const rollingBalances = JSON.parse(localStorage.getItem("rollingBalancesIC") || "[]");
      return rollingBalances.find(
        (element) => element.title.includes(Number(selectedYear) - 1)
      )?.value || user.startingBalanceIC || 0;
    }
  }
  
  const pcBalance = selectedYear === firstYear || selectedYear === "0" 
    ? (user.startingBalancePC || 0)
    : (JSON.parse(localStorage.getItem("rollingBalancesPC") || "[]").find(
        (element) => element.title.includes(Number(selectedYear) - 1)
      )?.value || user.startingBalancePC || 0);
      
  const icBalance = selectedYear === firstYear || selectedYear === "0"
    ? (user.startingBalanceIC || 0)
    : (JSON.parse(localStorage.getItem("rollingBalancesIC") || "[]").find(
        (element) => element.title.includes(Number(selectedYear) - 1)
      )?.value || user.startingBalanceIC || 0);
      
  return pcBalance + icBalance;
};

const aggregateTradesByPeriod = (trades, timeframe, selectedYear) => {
  const periodMap = new Map();
  
  if (trades.length === 0) {
    return [];
  }
  
  trades.forEach((trade) => {
    const date = moment(trade.entryDate, "DD/MM/YYYY");
    let periodKey;
    let periodLabel;
    
    switch (timeframe) {
      case "Weekly":
        if (selectedYear === "0") {
          const firstTradeDate = moment(trades[0].entryDate, "DD/MM/YYYY");
          const weeksDiff = date.diff(firstTradeDate.clone().startOf('isoWeek'), 'weeks');
          periodKey = weeksDiff + 1;
          periodLabel = `W${periodKey}`;
        } else {
          periodKey = date.isoWeek();
          periodLabel = `W${periodKey}`;
        }
        break;
        
      case "Monthly":
        if (selectedYear === "0") {
          periodKey = date.format("YYYY-MM");
          periodLabel = date.format("MMM YY");
        } else {
          periodKey = date.month();
          periodLabel = date.format("MMM");
        }
        break;
        
      case "Quarterly":
        periodKey = date.quarter() - 1;
        periodLabel = `Q${date.quarter()}`;
        break;
        
      case "Yearly":
        periodKey = date.year();
        periodLabel = date.year().toString();
        break;
        
      default:
        return;
    }
    
    if (!periodMap.has(periodKey)) {
      periodMap.set(periodKey, {
        label: periodLabel,
        trades: [],
        key: periodKey
      });
    }
    
    periodMap.get(periodKey).trades.push(trade);
  });
  
  return Array.from(periodMap.values()).sort((a, b) => {
    if (typeof a.key === 'string' && typeof b.key === 'string') {
      return a.key.localeCompare(b.key);
    }
    return a.key - b.key;
  });
};

const calculateCumulativeReturns = (periods, mode, showGross, startingBalance, backtestingMode) => {
  // In backtesting mode, trades use PC fields regardless of mode
  const isBacktest = backtestingMode || (periods.length > 0 && periods[0].trades.length > 0 && periods[0].trades[0].equityType === "Backtest");
  const personal = mode === "Personal Capital" || isBacktest;
  const investor = mode === "Investor Capital";
  const showAll = !personal && !investor;
  
  let previousBalance = startingBalance;
  
  const results = periods.map((period, index) => {
    const trades = period.trades;
    
    if (trades.length === 0) {
      return {
        label: period.label,
        percentage: 0,
        balance: previousBalance,
        periodReturn: 0,
        periodReturnPercent: 0
      };
    }
    
    const lastTrade = trades[trades.length - 1];
    
    let balance;
    let percentage;
    
    if (personal) {
      let rollingBalance;
      if (showGross) {
        rollingBalance = lastTrade.rollingBalancePCGross || 0;
      } else {
        rollingBalance = lastTrade.rollingBalancePC || 0;
      }
      
      // For backtesting, rolling balance is profit/loss only, need to add starting balance
      if (isBacktest) {
        balance = startingBalance + rollingBalance;
      } else {
        balance = rollingBalance;
      }
      
      // Calculate percentage from balance and starting balance
      // Don't rely on rollingPercent fields as they may be 0 for backtest trades
      if (startingBalance !== 0) {
        const totalReturn = balance - startingBalance;
        percentage = (totalReturn / startingBalance) * 100;
      } else {
        percentage = 0;
      }
    } else if (investor) {
      let rollingBalance;
      if (showGross) {
        rollingBalance = lastTrade.rollingBalanceICGross || 0;
      } else {
        rollingBalance = lastTrade.rollingBalanceIC || 0;
      }
      
      // For investor capital, rolling balance should already include starting balance
      // But check if it's abnormally low (might be profit/loss only)
      if (isBacktest) {
        balance = startingBalance + rollingBalance;
      } else {
        balance = rollingBalance;
      }
      
      // Calculate percentage from balance and starting balance
      if (startingBalance !== 0) {
        const totalReturn = balance - startingBalance;
        percentage = (totalReturn / startingBalance) * 100;
      } else {
        percentage = 0;
      }
    } else if (showAll) {
      let pcBalance = showGross 
        ? (lastTrade.rollingBalancePCGross ?? 0)
        : (lastTrade.rollingBalancePC ?? 0);
      let icBalance = showGross
        ? (lastTrade.rollingBalanceICGross ?? 0)
        : (lastTrade.rollingBalanceIC ?? 0);
      
      // For backtesting, rolling balances are profit/loss only, need to add starting balance
      if (isBacktest) {
        // In backtest mode, only PC balance is used (IC is always 0)
        balance = startingBalance + pcBalance;
      } else {
        balance = pcBalance + icBalance;
      }
      
      // Calculate cumulative percentage based on starting balance and current balance
      if (startingBalance !== 0) {
        const totalReturn = balance - startingBalance;
        percentage = (totalReturn / startingBalance) * 100;
      } else {
        percentage = 0;
      }
    }
    
    const periodReturn = balance - previousBalance;
    const periodReturnPercent = previousBalance !== 0 
      ? (periodReturn / previousBalance) * 100 
      : 0;
    
    previousBalance = balance;
    
    return {
      label: period.label,
      percentage: percentage,
      balance: balance,
      periodReturn: periodReturn,
      periodReturnPercent: periodReturnPercent
    };
  });
  
  return results;
};

export const processEquityCurveData = async ({
  allTrades,
  calculateFields,
  backtestingMode,
  backTestId,
  selectedAccountId,
  timeframe,
  selectedYear,
  mode,
  showGross,
  allYearsOfTrades
}) => {
  let filteredTrades = allTrades;

  if (!backtestingMode) {
    filteredTrades = getFilteredTradesByAccount(allTrades, selectedAccountId);
  }
  
  let trades = await calculateFields(
    filteredTrades,
    backtestingMode,
    backtestingMode ? backTestId : "",
    false,
    true
  );
  
  if (backtestingMode) {
    if (backTestId === "Missed Trades") {
      trades = trades.filter((t) => t.missedTrade);
    } else if (backTestId && backTestId !== "null" && backTestId !== "undefined") {
      trades = trades.filter((t) => t.testId === backTestId);
    }
  }
  
  trades = trades.filter(
    (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
  );
  
  if (selectedYear !== "0" && (!backtestingMode || backTestId === "Missed Trades")) {
    trades = trades.filter(
      (t) => moment(t.entryDate, "DD/MM/YYYY").format("Y") === selectedYear
    );
  }
  
  trades.sort((a, b) => {
    const dateA = moment(a.entryDate, "DD/MM/YYYY");
    const dateB = moment(b.entryDate, "DD/MM/YYYY");
    return dateA.diff(dateB);
  });
  
  const safeAllYears = allYearsOfTrades || [];
  const startingBalance = getStartingBalance(mode, backtestingMode, safeAllYears, selectedYear);
  
  const periods = aggregateTradesByPeriod(trades, timeframe, selectedYear);
  
  const chartData = calculateCumulativeReturns(periods, mode, showGross, startingBalance, backtestingMode);
  
  return chartData;
};
